// Load one bundle and decide which pipeline it needs, before any decoder is built.
// Notes: ~/comments/aos-threejs-poc/useOgsBoot_load_then_classify.md

import { useEffect, useState } from 'react'
import { loadOgsContentPacks } from '../../lib/ogsContentPacks.js'
import { loadMultiRegionScene } from '../../ogs/assets/loadMultiRegionScene.ts'
import { resolvePipelineKind } from '../../ogs/pipeline/pipelineKind.ts'
import { publishDebug, retractDebug } from '../../ogs/pipeline/ogsDebug.ts'

const IDLE = { state: 'idle', packs: null, scene: null, kind: null, why: null, error: null }

/** `{ state, packs, scene, kind, why, error }`. Loud on failure — never a partial bundle. */
export function useOgsBoot({ slug, log }) {
  const [boot, setBoot] = useState(IDLE)

  useEffect(() => {
    let cancelled = false
    const publish = (next) => {
      publishDebug('__OGS_BOOT__', {
        state: next.state, slug, kind: next.kind, why: next.why, error: next.error,
      })
      if (!cancelled) setBoot(next)
    }
    publish({ ...IDLE, state: 'loading' })

    ;(async () => {
      const packs = await loadOgsContentPacks(slug)
      if (cancelled) return
      // A mesh-only bundle has no scene.json to classify from; the kind says so.
      const scene = packs.meshOnly ? null : await loadMultiRegionScene(packs.texBase, packs.scene)
      if (cancelled) return
      const resolved = resolvePipelineKind({
        manifest: scene?.manifest ?? null,
        rig: packs.rigPlan?.rig ?? 'none',
        meshOnly: !!packs.meshOnly,
      })
      log.info(`'${slug}': ${resolved.kind} — ${resolved.why}`)
      publish({ state: 'ready', packs, scene, kind: resolved.kind, why: resolved.why, error: null })
    })().catch((error) => {
      if (cancelled) return
      const message = error?.message ?? String(error)
      log.error(`'${slug}': bundle load failed — ${message}`)
      publish({ ...IDLE, state: 'error', error: message })
    })

    return () => { cancelled = true; retractDebug('__OGS_BOOT__') }
  }, [slug, log])

  return boot
}
